import { API_BASE_URL } from '@/shared/config/env'

/**
 * Запросы приложения CRM и окна ведущего встречи из CRM.
 *
 * Не через общий ApiHandler: его перехватчик на любой 401 стирает вход ROOMS
 * и уводит на главную, а здесь 401 — обычный ответ («сеанс CRM истёк»,
 * «сессия ведущего закончилась»).
 *  • вкладка карточки — без кук (в iframe чужого сайта они не работают),
 *    сеанс CRM — в теле запроса;
 *  • окно ведущего — с HttpOnly-cookie сессии (credentials: 'include') и
 *    заголовком X-Requested-With; только на путях host-session; встреча окна —
 *    в заголовке X-Rooms-Host-Meeting (окна разных встреч в одном браузере);
 *  • страница просмотра записи и переписки — так же, со своей cookie на путях crm/view;
 *  • переписка встречи у гостя — без кук, по токену переписки в теле запроса
 *    (на 401 «нет токена» окно встречи получает новый, а не выходит из ROOMS).
 */

/** Ошибка в том же виде, что у axios (error.response.status / data). */
export class CrmApiError extends Error {
  public readonly response: { status: number; data: any }

  constructor(status: number, data: any) {
    super(String(data?.message || `HTTP ${status}`))
    this.response = { status, data }
  }

  get status(): number { return this.response.status }

  get code(): string { return String(this.response.data?.code || '') }
}

const HOST_SESSION_PATH = 'integrations/host-session/'
const HOST_MEETING_KEY = 'rooms_host_meeting'

/**
 * Встреча этого окна ведущего. В одном браузере могут быть открыты окна разных
 * встреч, а cookie сессии одна на всех: окно называет свою встречу в каждом
 * запросе, и сервер берёт её сессию. Хранится во вкладке — переживает перезагрузку.
 */
function hostMeeting(): string {
  try { return sessionStorage.getItem(HOST_MEETING_KEY) || '' } catch { return '' }
}

function rememberHostMeeting(info: any): void {
  const id = info?.meeting_id
  if (typeof id !== 'number') return
  try { sessionStorage.setItem(HOST_MEETING_KEY, String(id)) } catch { /* приватный режим — сервер возьмёт последнюю сессию */ }
}

/** withSession — запрос по HttpOnly-cookie сессии (окно ведущего, просмотр записи). */
async function post(path: string, body: any, withSession: boolean): Promise<any> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', Accept: 'application/json' }
  if (withSession) headers['X-Requested-With'] = 'XMLHttpRequest'
  if (withSession && path.startsWith(HOST_SESSION_PATH) && path !== HOST_SESSION_PATH + 'redeem' && hostMeeting()) {
    headers['X-Rooms-Host-Meeting'] = hostMeeting()
  }
  let res: Response
  try {
    res = await fetch(API_BASE_URL + path, {
      method: 'POST',
      headers,
      body: JSON.stringify(body ?? {}),
      credentials: withSession ? 'include' : 'omit',
      referrerPolicy: 'no-referrer',
    })
  } catch {
    throw new CrmApiError(0, { message: 'Нет связи с ROOMS. Проверьте интернет.' })
  }
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new CrmApiError(res.status, data)
  return data
}

/** Состояние отправки итога встречи в CRM. */
export interface CrmSync {
  state: 'none' | 'pending' | 'sent' | 'dead'
  last_error: string | null
}

/** Сводка записей встречи (сегменты не в «failed»). */
export interface CrmMeetingRecordings {
  count: number
  ready: number
  recording_now: boolean
}

/** Встреча на вкладке карточки. */
export interface CrmMeeting {
  id: number
  title: string
  state: 'scheduled' | 'live' | 'done' | 'no_show' | 'canceled'
  starts_at: string
  ends_at: string
  duration_minutes: number
  waiting_room: boolean
  guest_url: string
  host_name: string | null
  contact_seconds: number | null
  from_converted: boolean
  crm_sync?: CrmSync
  recordings?: CrmMeetingRecordings
  /** Переписка встречи: stored — сохраняется; count — не удалённые сообщения. */
  chat?: { policy: 'ephemeral' | 'stored'; count: number }
}

export interface CrmTabState {
  user: { name: string | null }
  entity: { type: string; id: string; title: string }
  recording_policy: string
  meetings: CrmMeeting[]
}

/** Подтверждение запроса вкладки: сеанс CRM и место встраивания, как их дала CRM. */
export interface CrmTabAuth {
  auth: Record<string, string>
  placement: Record<string, any>
}

export interface HostSessionInfo {
  role: 'host'
  /** Встреча сессии — окно называет её в запросах (X-Rooms-Host-Meeting). */
  meeting_id?: number
  title: string
  starts_at: string
  ends_at: string
  name: string | null
  state: 'open' | 'canceled' | 'ended'
  message: string | null
  waiting_room: boolean
  recording_policy: string
  /** false — запись на почту гостям не предлагается (встреча из CRM). */
  recording_by_email?: boolean
  /** stored — чат встречи сохраняется и идёт через API, а не через канал звонка. */
  chat_policy?: 'ephemeral' | 'stored'
  chat?: { my_tag: string }
}

/** Фрагмент записи встречи: каждое включение записи — отдельный. */
export interface RecordingSegment {
  id: number
  segment_no: number
  status: 'recording' | 'processing' | 'ready' | 'failed'
  started_at: string
  ended_at: string | null
  duration_sec: number | null
  started_by_name: string | null
}

/**
 * Запись встречи в окне ведущего. «starting» — запись запрошена, медиасервер
 * её ещё не подтвердил; «active» — подтвердил, индикатор видят все.
 */
export interface RecordingState {
  policy: 'host' | 'disabled'
  state: 'off' | 'starting' | 'active' | 'failed'
  current: { id: number; segment_no: number; started_at: string; started_by_name: string | null } | null
  error: string | null
  segments: RecordingSegment[]
}

/** Фрагмент на странице просмотра записи. */
export interface ViewRecording {
  id: number
  segment_no: number
  status: 'ready' | 'processing' | 'recording'
  started_at: string
  ended_at: string | null
  duration_sec: number | null
  size_bytes: number | null
  started_by_name: string | null
  expires_at: string | null
  stream_url: string
}

/** Страница просмотра записи встречи (по cookie сессии просмотра). */
export interface RecordingView {
  kind: 'recording'
  meeting: { id: number; title: string; starts_at: string; started_at: string | null; ended_at: string | null }
  viewer: { name: string | null }
  session_expires_at: string
  recordings: ViewRecording[]
}

/** Сообщение сохраняемой переписки встречи. mine — только в ответах участнику. */
export interface MeetingChatMessage {
  id: number
  author_kind: 'host' | 'guest'
  author_name: string
  /** Непрозрачная метка автора; своя — my_tag. */
  author_tag: string
  mine?: boolean
  /** null у удалённого. */
  text: string | null
  created_at: string
  edited_at: string | null
  deleted: boolean
  /** Метка отправки из окна автора — только у своих и в рассылке. */
  client_id?: string | null
}

export interface MeetingChatList {
  data: MeetingChatMessage[]
  my_tag: string
  /** false — встреча не идёт: только чтение. */
  can_post: boolean
}

/** Страница переписки встречи для сотрудника (по cookie сессии просмотра). */
export interface ChatView {
  kind: 'chat'
  meeting: { id: number; title: string; starts_at: string; started_at: string | null; ended_at: string | null }
  viewer: { name: string | null }
  session_expires_at: string
  retention_days: number
  messages: MeetingChatMessage[]
}

/** Итог встречи, заполненный ведущим. */
export interface MeetingOutcome {
  summary: string
  next_step: string | null
  next_step_due: string | null
  no_show: boolean
  submitted_at: string | null
}

export interface MeetingOutcomePayload {
  summary: string
  next_step: string
  /** 'YYYY-MM-DD' или пусто. */
  next_step_due: string
  no_show: boolean
}

export interface HostOutcomeState {
  outcome: MeetingOutcome | null
  no_show_suggested: boolean
  crm_sync: CrmSync
}

export abstract class CrmApi {
  public static install(provider: string, auth: Record<string, any>) {
    return post(`crm/${provider}/install`, { auth }, false)
  }

  public static tab(provider: string, a: CrmTabAuth): Promise<CrmTabState> {
    return post(`crm/${provider}/tab`, a, false)
  }

  public static schedule(provider: string, a: CrmTabAuth, payload: { starts_at: string; duration_minutes: number; waiting_room: boolean }): Promise<CrmMeeting> {
    return post(`crm/${provider}/tab/meetings`, { ...a, ...payload }, false)
  }

  public static reschedule(provider: string, a: CrmTabAuth, id: number, payload: { starts_at: string; duration_minutes: number }): Promise<CrmMeeting> {
    return post(`crm/${provider}/tab/meetings/${id}/reschedule`, { ...a, ...payload }, false)
  }

  public static cancel(provider: string, a: CrmTabAuth, id: number): Promise<CrmMeeting> {
    return post(`crm/${provider}/tab/meetings/${id}/cancel`, a, false)
  }

  public static hostLaunch(provider: string, a: CrmTabAuth, id: number): Promise<{ launch_url: string; expires_at: string }> {
    return post(`crm/${provider}/tab/meetings/${id}/host-launch`, a, false)
  }

  /** Повторить отправку итога встречи в CRM; ответ — встреча. */
  public static crmRetry(provider: string, a: CrmTabAuth, id: number): Promise<CrmMeeting> {
    return post(`crm/${provider}/tab/meetings/${id}/crm-retry`, a, false)
  }

  /** Одноразовая ссылка на страницу записи встречи (действует минуту). */
  public static recordingViewLink(provider: string, a: CrmTabAuth, meetingId: number): Promise<{ view_url: string; expires_at: string }> {
    return post(`crm/${provider}/recordings/view-link`, { ...a, meeting_id: meetingId }, false)
  }

  /** Одноразовая ссылка на страницу переписки встречи (действует минуту). */
  public static chatViewLink(provider: string, a: CrmTabAuth, meetingId: number): Promise<{ view_url: string; expires_at: string }> {
    return post(`crm/${provider}/chat/view-link`, { ...a, meeting_id: meetingId }, false)
  }

  // --- Окно ведущего (cookie сессии) ---

  public static async hostRedeem(code: string): Promise<HostSessionInfo> {
    const info = await post('integrations/host-session/redeem', { code }, true)
    rememberHostMeeting(info)
    return info
  }

  public static async hostCurrent(): Promise<HostSessionInfo> {
    const info = await post('integrations/host-session/current', {}, true)
    rememberHostMeeting(info)
    return info
  }

  /** Ответ того же вида, что вход по ссылке: токен или ожидание начала. */
  public static hostToken(name: string): Promise<any> {
    return post('integrations/host-session/livekit-token', { name }, true)
  }

  public static hostWaiting(): Promise<any> {
    return post('integrations/host-session/waiting', {}, true)
  }

  public static hostAdmit(id: number) {
    return post(`integrations/host-session/admissions/${id}/admit`, {}, true)
  }

  public static hostDeny(id: number) {
    return post(`integrations/host-session/admissions/${id}/deny`, {}, true)
  }

  /** Итог встречи (если уже отправлен), подсказка «клиент не пришёл» и состояние отправки в CRM. */
  public static hostOutcomeState(): Promise<HostOutcomeState> {
    return post('integrations/host-session/outcome-state', {}, true)
  }

  /** Отправить или исправить итог; 422 — ошибки полей в errors. */
  public static hostOutcome(payload: MeetingOutcomePayload): Promise<{ outcome: MeetingOutcome; crm_sync: CrmSync }> {
    return post('integrations/host-session/outcome', payload, true)
  }

  /** Включить запись; уже идущая или только что запрошенная вернётся без нового запуска. */
  public static hostRecordingStart(): Promise<{ id: number; segment_no: number; status: string; state: RecordingState['state'] }> {
    return post('integrations/host-session/recording/start', {}, true)
  }

  /** Без id — остановить текущий фрагмент встречи; повтор безопасен. */
  public static hostRecordingStop(id?: number): Promise<{ ok: boolean; id: number; status: string; state: RecordingState['state'] }> {
    return post('integrations/host-session/recording/stop', id ? { id } : {}, true)
  }

  public static hostRecordingState(): Promise<RecordingState> {
    return post('integrations/host-session/recording/state', {}, true)
  }

  // --- Переписка встречи: ведущий (cookie сессии) ---

  public static hostChatList(): Promise<MeetingChatList> {
    return post('integrations/host-session/chat/list', {}, true)
  }

  /** client_id — повторная отправка того же сообщения вернёт его же, без дубля. */
  public static hostChatSend(text: string, clientId: string): Promise<{ message: MeetingChatMessage }> {
    return post('integrations/host-session/chat/messages', { text, client_id: clientId }, true)
  }

  public static hostChatUpdate(id: number, text: string): Promise<{ message: MeetingChatMessage }> {
    return post(`integrations/host-session/chat/messages/${id}/update`, { text }, true)
  }

  public static hostChatDelete(id: number): Promise<{ message: MeetingChatMessage }> {
    return post(`integrations/host-session/chat/messages/${id}/delete`, {}, true)
  }

  // --- Переписка встречи: гость (по коду ссылки и токену переписки) ---

  public static guestChatList(code: string, chatToken: string): Promise<MeetingChatList> {
    return post(`integrations/links/${encodeURIComponent(code)}/chat/list`, { chat_token: chatToken }, false)
  }

  public static guestChatSend(code: string, chatToken: string, text: string, clientId: string): Promise<{ message: MeetingChatMessage }> {
    return post(`integrations/links/${encodeURIComponent(code)}/chat/messages`, { chat_token: chatToken, text, client_id: clientId }, false)
  }

  public static guestChatUpdate(code: string, chatToken: string, id: number, text: string): Promise<{ message: MeetingChatMessage }> {
    return post(`integrations/links/${encodeURIComponent(code)}/chat/messages/${id}/update`, { chat_token: chatToken, text }, false)
  }

  public static guestChatDelete(code: string, chatToken: string, id: number): Promise<{ message: MeetingChatMessage }> {
    return post(`integrations/links/${encodeURIComponent(code)}/chat/messages/${id}/delete`, { chat_token: chatToken }, false)
  }

  // --- Страница записи и переписки встречи (cookie сессии просмотра) ---
  // У записи и переписки свои cookie просмотра. Код ссылки сам знает, что
  // открывает; при перезагрузке страница говорит, чья сессия ей нужна
  // (без kind — запись, как раньше).

  public static viewRedeem(code: string): Promise<RecordingView | ChatView> {
    return post('crm/view/redeem', { code }, true)
  }

  public static viewCurrent(kind?: 'recording' | 'chat'): Promise<RecordingView | ChatView> {
    return post('crm/view/current', kind ? { kind } : {}, true)
  }
}

/** Сотрудника отключили в CRM: сервер отозвал сессию, вернуться нельзя. */
export function isCrmUserInactive(e: unknown): boolean {
  return e instanceof CrmApiError && e.status === 403 && e.code === 'CRM_USER_INACTIVE'
}
